import React from "react";
import {
  TASK_STATUSES, STATUS_LABELS,
  FUNCTIONAL_ROLE_LABELS, FUNCTIONAL_ROLE_COLORS,
} from "./df-constants";
import { api } from "../../lib/api";

function StatusChip({ status }) {
  return <span className={`df-chip df-chip-${status}`}>{STATUS_LABELS[status]}</span>;
}

function PriorityDot({ priority }) {
  return <span className={`df-prio-${priority} text-xs flex-shrink-0`} title={`Priority: ${priority}`}>●</span>;
}

function ProgressBar({ done, total }) {
  if (!total) return null;
  const pct = Math.round((done / total) * 100);
  return (
    <div className="hidden md:flex items-center gap-2 flex-shrink-0">
      <div className="w-16 h-1.5 bg-ink-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-ink-500 font-mono w-8 text-right">{pct}%</span>
    </div>
  );
}

function checklistTotals(task) {
  let total = (task.checklistStats?.total) ?? (task.checklist?.length ?? 0);
  let done = (task.checklistStats?.done) ?? ((task.checklist || []).filter((c) => c.done).length);
  for (const s of task.subtasks || []) {
    total += (s.checklist?.length || s.checklistStats?.total || 0);
    done += ((s.checklist || []).filter((c) => c.done).length || s.checklistStats?.done || 0);
  }
  return { total, done };
}

function TaskRow({ task, members, onOpen, onChange }) {
  const overdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== "done";

  async function patch(body) {
    try {
      const optimistic = { ...task, ...body };
      if (body.completed !== undefined) optimistic.status = body.completed ? "done" : "todo";
      onChange && onChange(optimistic);
      await api(`/tasks/${task._id}`, { method: "PATCH", body: JSON.stringify(body) });
    } catch (e) {
      onChange && onChange(task);
    }
  }

  const totals = checklistTotals(task);
  const dueStr = task.dueDate ? task.dueDate.slice(0, 10) : "";
  const isDone = task.status === "done";
  const assigneeId = (task.assignees && task.assignees[0]?._id) || "";

  return (
    <div className="group flex items-center gap-3 px-3 py-2 df-row-hover">
      <button
        onClick={(e) => { e.stopPropagation(); patch({ completed: !isDone }); }}
        className={[
          "w-4 h-4 rounded-full border-2 flex items-center justify-center transition flex-shrink-0",
          isDone ? "bg-emerald-500 border-emerald-500" : "border-ink-300 hover:border-emerald-500",
        ].join(" ")}
        title="Toggle complete"
      >
        {isDone && (
          <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white">
            <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <PriorityDot priority={task.priority} />

      <button
        onClick={onOpen}
        className={[
          "flex-1 text-left text-sm truncate min-w-0",
          isDone ? "line-through text-ink-400" : "text-ink-800",
        ].join(" ")}
      >
        {task.title}
      </button>

      <ProgressBar done={totals.done} total={totals.total} />

      {(task.labels || []).slice(0, 2).map((l) => (
        <span key={l} className="hidden md:inline-flex items-center px-2 py-0.5 text-[10px] rounded-full bg-ink-100 text-ink-600 flex-shrink-0">{l}</span>
      ))}

      {(task.comments || []).length > 0 && (
        <span className="text-[10px] text-ink-400 flex-shrink-0">💬 {task.comments.length}</span>
      )}

      <select
        value={assigneeId}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => patch({ assignees: e.target.value ? [e.target.value] : [] })}
        className="text-[11px] text-ink-600 bg-transparent border-0 outline-none w-32 truncate flex-shrink-0 cursor-pointer"
        title="Assignee"
      >
        <option value="">Unassigned</option>
        {members.map((m) => <option key={m._id} value={m._id}>{m.username}</option>)}
      </select>

      <input
        type="date"
        value={dueStr}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => patch({ dueDate: e.target.value || null })}
        className={[
          "text-[11px] bg-transparent border-0 outline-none w-28 flex-shrink-0",
          overdue ? "text-red-500 font-semibold" : "text-ink-500",
        ].join(" ")}
      />

      <select
        value={task.status}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => patch({ status: e.target.value })}
        className={`df-chip df-chip-${task.status} cursor-pointer border-0 outline-none flex-shrink-0`}
      >
        {TASK_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
      </select>
    </div>
  );
}

function DeptSubtaskList({ subtasks, onOpenTask }) {
  const dept = (subtasks || []).filter((s) => s.department);
  if (dept.length === 0) return null;
  return (
    <div className="bg-ink-50/60 border-t border-ink-100">
      {dept.map((s) => (
        <button
          key={s._id}
          onClick={() => onOpenTask(s._id)}
          className="w-full flex items-center gap-3 pl-10 pr-3 py-2 text-left hover:bg-white"
        >
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: FUNCTIONAL_ROLE_COLORS[s.department] }} />
          <span className="text-xs font-medium text-ink-700 w-32 flex-shrink-0">
            {FUNCTIONAL_ROLE_LABELS[s.department]}
          </span>
          <span className="text-xs text-ink-500 flex-1 truncate">
            {(s.assignees && s.assignees[0]?.username) || "Unassigned"}
          </span>
          {(s.comments || []).length > 0 && (
            <span className="text-[10px] text-ink-400">💬 {s.comments.length}</span>
          )}
          <span className={`df-chip df-chip-${s.status}`}>{STATUS_LABELS[s.status]}</span>
        </button>
      ))}
    </div>
  );
}

export default function DfListView({ tasks, members = [], onOpenTask, onTasksChange }) {
  const grouped = TASK_STATUSES.map((s) => ({
    status: s,
    tasks: (tasks || []).filter((t) => t.status === s && !t.parentTaskId),
  }));

  function patchOne(updated) {
    if (!onTasksChange) return;
    onTasksChange((tasks || []).map((t) => (t._id === updated._id ? updated : t)));
  }

  return (
    <div className="max-w-6xl mx-auto">
      {grouped.map((g) => (
        <section key={g.status} className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <StatusChip status={g.status} />
            <span className="text-xs text-ink-400">{g.tasks.length}</span>
          </div>
          <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
            {g.tasks.length === 0 ? (
              <div className="px-4 py-3 text-xs text-ink-400 italic">No tasks</div>
            ) : (
              g.tasks.map((t, i) => (
                <div key={t._id} className={i > 0 ? "border-t border-ink-100" : ""}>
                  <TaskRow
                    task={t} members={members}
                    onOpen={() => onOpenTask(t._id)}
                    onChange={patchOne}
                  />
                  {t.subtasks && t.subtasks.length > 0 && (
                    <DeptSubtaskList subtasks={t.subtasks} onOpenTask={onOpenTask} />
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

export { StatusChip, PriorityDot };
